import dayjs from 'dayjs';
import 'dayjs/locale/pt-br';
import { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { Grid, Typography, TextField, Paper, InputAdornment } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';

import { acGetAllCreditPolicies, acGetCreditPolicy } from '../../store/actions';
import { ROUTES } from '../../store/constants';
import { DataTable, Loader, PrimaryButton } from '../default';
import ActivateCreditPolicyModal from '../modals/ActivateCreditPolicyModal';

const CreditPoliciesTable = () => {

    const dispatch = useDispatch();
    const navigate = useNavigate();

    const [openSwitchModal, setOpenSwitchModal] = useState(false);

    const [search, setSearch] = useState('');
    const [date, setDate] = useState(null);

    const allCreditPolicies = useSelector(state => state.creditPolicies.allCreditPolicies);
    const isLoadingAllCreditPolicies = useSelector(state => state.creditPolicies.isLoadingAllCreditPolicies);
    const hasErrorAllCreditPolicies = useSelector(state => state.creditPolicies.hasErrorAllCreditPolicies);

    const selectedCreditPolicy = useSelector(state => state.creditPolicies.selectedCreditPolicy);

    const isCreatingCreditPolicy = useSelector(state => state.creditPolicies.isCreatingCreditPolicy);
    const isUpdatingCreditPolicy = useSelector(state => state.creditPolicies.isUpdatingCreditPolicy);
    const isDeletingCreditPolicy = useSelector(state => state.creditPolicies.isDeletingCreditPolicy);

    useEffect(() => {
        dispatch(acGetAllCreditPolicies())
            .catch(error => console.error(error))
        // eslint-disable-next-line
    }, [])

    const filteredPolicies = (allCreditPolicies || []).filter(policy => {
        const matchesSearch = !search ||
            policy.name?.toLowerCase().includes(search.toLowerCase()) ||
            policy.description?.toLowerCase().includes(search.toLowerCase());
        const matchesDate = !date ||
            (policy.createdAt &&
                dayjs(policy.createdAt).format('YYYY-MM-DD') === dayjs(date).format('YYYY-MM-DD'));
        return matchesSearch && matchesDate;
    });

    const columns = [
        {
            accessorKey: 'credit_policy_id',
            header: 'ID',
            enableEditing: false,
            size: 80
        },
        {
            accessorKey: 'name',
            header: 'Nome'
        },
        {
            accessorKey: 'description',
            header: 'Descrição'
        },
        {
            accessorKey: 'createdAt',
            header: 'Data de Criação',
            Cell: ({ cell }) => renderDateColumn(cell)
        }
    ]

    const renderDateColumn = (cell) => {
        const date = dayjs(cell.getValue())
        return date.locale('pt-br').format('DD/MM/YYYY HH:mm')
    }

    const onCreate = () => {
        navigate(ROUTES.CREDIT_POLICIES_CREATE)
    }

    const onUpdate = (row) => {
        dispatch(acGetCreditPolicy(row.original.credit_policy_id))
            .then(selectedCreditPolicy => {
                navigate(ROUTES.CREDIT_POLICIES_CREATE, {
                    state: {
                        selectedCreditPolicy: selectedCreditPolicy
                    }
                })
            })
            .catch(error => console.error(error))
    }

    const onSwitch = (row) => {
        dispatch(acGetCreditPolicy(row.original.credit_policy_id))
            .then(() => {
                setOpenSwitchModal(!openSwitchModal)
            })
            .catch(error => console.error(error))
    }

    return (
        <Paper elevation={0} sx={{ padding: 3 }}>
            <Typography variant="h6" fontWeight="bold">
                Gestão de Políticas de Crédito
            </Typography>
            <Typography variant="body2" sx={{ mb: 3 }}>
                Consulte e gerencie as políticas de crédito configuradas para seus clientes.
            </Typography>

            {!allCreditPolicies ? (
                <Loader color='primary' />
            ) : (
                <>
                    <Grid container spacing={2} alignItems="center" sx={{ mb: 2 }}>
                        <Grid item flexGrow={1}>
                            <TextField
                                fullWidth
                                placeholder="Buscar política de crédito"
                                value={search}
                                onChange={e => setSearch(e.target.value)}
                                InputProps={{
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <SearchIcon />
                                        </InputAdornment>
                                    )
                                }}
                                size="small"
                            />
                        </Grid>
                        <Grid item xs='auto'>
                            <DatePicker
                                label="Selecione a data"
                                value={date}
                                onChange={setDate}
                                format="DD/MM/YYYY"
                                slotProps={{
                                    textField: { size: 'small', fullWidth: true },
                                    actionBar: { actions: ['clear'] }
                                }}
                            />
                        </Grid>
                        <Grid item xs='auto'>
                            <PrimaryButton
                                label="Criar Política"
                                Icon={AddIcon}
                                onClick={onCreate}
                            />
                        </Grid>
                    </Grid>

                    <ActivateCreditPolicyModal
                        open={openSwitchModal}
                        onClose={() => setOpenSwitchModal(!openSwitchModal)}
                        selectedCreditPolicy={selectedCreditPolicy}
                    />
                    <DataTable
                        resource='Política de Crédito'
                        gender='fem'
                        tableType='editAndSwitch'
                        columns={columns}
                        data={filteredPolicies}
                        sortingId='credit_policy_id'
                        onCreate={onCreate}
                        onUpdate={onUpdate}
                        onSwitch={onSwitch}
                        isLoading={isLoadingAllCreditPolicies}
                        isSaving={isCreatingCreditPolicy || isUpdatingCreditPolicy || isDeletingCreditPolicy}
                        showAlertBanner={hasErrorAllCreditPolicies}
                        showButton={false}
                    />
                </>
            )}
        </Paper>
    )
}

export default CreditPoliciesTable
